/**
 * Modalità "Confronta release" della pagina prodotto: l'utente sceglie due release e vede le
 * differenze "stile git diff" — metadati della release, bundle, file BOM e componenti dichiarati
 * nei BOM (confronto profondo calcolato da product-service).
 */

import { useState } from 'react';
import { useReleaseComparison } from './hooks.js';
import { diffLineText, releaseDiffLines, releaseDiffSummary } from './releaseCompare.js';
import styles from './Products.module.css';

export function ReleaseCompare({
  productId,
  versions,
  onClose,
}: {
  productId: string;
  /** Versioni selezionabili, nell'ordine della lista release. */
  versions: string[];
  onClose: () => void;
}) {
  const [da, setDa] = useState('');
  const [a, setA] = useState('');
  const stesse = da !== '' && da === a;
  const { data, isLoading, isError, error } = useReleaseComparison(productId, da, a);

  return (
    <section className={styles.compareBox} data-testid="confronto-release" aria-label="Confronto">
      <div className={styles.compareBar}>
        <span className={styles.compareTitle}>Confronta release</span>
        <label className={styles.compareField}>
          Da
          <select
            className={styles.formSelect}
            value={da}
            onChange={(e) => setDa(e.target.value)}
            data-testid="select-confronto-da"
          >
            <option value="">— scegli —</option>
            {versions.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={`${styles.btnSecondary} ${styles.btnSmall}`}
          onClick={() => {
            setDa(a);
            setA(da);
          }}
          disabled={da === '' && a === ''}
          title="Inverti le due release"
          aria-label="Inverti le due release"
          data-testid="btn-inverti-confronto"
        >
          ⇄
        </button>
        <label className={styles.compareField}>
          A
          <select
            className={styles.formSelect}
            value={a}
            onChange={(e) => setA(e.target.value)}
            data-testid="select-confronto-a"
          >
            <option value="">— scegli —</option>
            {versions.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={`${styles.btnSecondary} ${styles.btnSmall}`}
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          data-testid="btn-esci-confronto"
        >
          Esci dal confronto
        </button>
      </div>

      {(da === '' || a === '') && (
        <p className={styles.compareHint} data-testid="confronto-istruzioni">
          Scegli le due release da confrontare: vedrai le differenze di metadati, bundle, file BOM e
          componenti.
        </p>
      )}
      {stesse && (
        <p className={styles.compareHint} data-testid="confronto-stessa-release">
          Scegli due release diverse.
        </p>
      )}
      {isLoading && <p className={styles.loadingMsg}>Confronto in corso…</p>}
      {isError && (
        <p className={styles.errorMsg} data-testid="confronto-errore">
          {error instanceof Error ? error.message : 'Errore nel confronto delle release'}
        </p>
      )}
      {data && !stesse && (
        <>
          <p className={styles.compareSummary} data-testid="confronto-riepilogo">
            {releaseDiffSummary(data)}
          </p>
          <div className={styles.diffScroll}>
            <pre className={styles.diff} data-testid="diff-release">
              {releaseDiffLines(data).map((riga, i) => (
                <span
                  key={i}
                  className={`${styles.diffLine} ${styles[`diff_${riga.kind}`] ?? ''}`}
                  data-kind={riga.kind}
                >
                  {diffLineText(riga)}
                  {'\n'}
                </span>
              ))}
            </pre>
          </div>
        </>
      )}
    </section>
  );
}
